import { z } from 'zod';
import { macroTreeGraphDraftSchema, macroTriggerSchema, type MacroTreeGraphDraft } from '../macroTree';
import { macroSourceGraphSchema, type MacroSourceGraph } from './sourceGraph';
import { sha256HexSchema } from './canonicalJson';
import { campaignMacroRuntimeSchema, compileDiagnosticSchema, type CampaignMacroRuntime } from './workflow';

const revision = z.number().int().safe().nonnegative();
export const macroMigrationReportSchema = z.object({ campaignId: z.string().min(1).max(128),
  trigger: macroTriggerSchema, legacyRevision: revision,
  status: z.enum(['eligible', 'blocked', 'draft-created', 'already-migrated']), sourceRev: revision.optional(),
  missingEvidence: z.boolean(), diagnostics: z.array(compileDiagnosticSchema).max(10_000) }).strict()
  .refine(value => !['draft-created', 'already-migrated'].includes(value.status) || (value.sourceRev ?? 0) > 0,
    'Created drafts require a positive source revision');
export type MacroMigrationReport = z.infer<typeof macroMigrationReportSchema>;
const archivedGraph = z.object({ draft: macroSourceGraphSchema.nullable(), locked: macroSourceGraphSchema.nullable(),
  sourceRev: revision, runtime: campaignMacroRuntimeSchema }).strict();
const archivePath = z.string().min(1).max(1024).refine(path => {
  if (/[\\?#%:]/.test(path)) return false;
  const parts = path.split('/');
  if (parts.some(part => !part || part === '.' || part === '..')) return false;
  if (parts[0] === 'legacy') return parts.length >= 2;
  if (parts[0] === 'raw') return parts.length >= 3 && z.string().uuid().safeParse(parts[1]).success;
  return ['compiled', 'analysis'].includes(parts[0]) && parts.length >= 3 && sha256HexSchema.safeParse(parts[1]).success;
}, 'Archive inventory paths must stay within a supported relative asset namespace');
export interface CampaignMacroArchive {
  format: 'playcast.campaign.macros'; formatVersion: 1; sourceCampaignId: string;
  graphs: Partial<Record<'launch' | 'watcher', { draft: MacroSourceGraph | null; locked: MacroSourceGraph | null;
    sourceRev: number; runtime: CampaignMacroRuntime }>>;
  legacyTrees?: Partial<Record<'launch' | 'watcher', { revision: number; graph: MacroTreeGraphDraft }>>;
  blobs: { path: string; sha256: string; bytes: number; contentType: string }[];
}
export const campaignMacroArchiveSchema: z.ZodType<CampaignMacroArchive, z.ZodTypeDef, unknown> = z.object({ format: z.literal('playcast.campaign.macros'), formatVersion: z.literal(1),
  sourceCampaignId: z.string().min(1).max(128),
  graphs: z.object({ launch: archivedGraph.optional(), watcher: archivedGraph.optional() }).strict(),
  legacyTrees: z.object({ launch: z.object({ revision, graph: macroTreeGraphDraftSchema }).strict().optional(),
    watcher: z.object({ revision, graph: macroTreeGraphDraftSchema }).strict().optional() }).strict().optional(),
  blobs: z.array(z.object({ path: archivePath, sha256: sha256HexSchema,
    bytes: z.number().int().safe().nonnegative(), contentType: z.string().min(1).max(128) }).strict()),
}).strict().superRefine((value, context) => {
  if (new Set(value.blobs.map(blob => blob.path)).size !== value.blobs.length) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['blobs'], message: 'Archive paths must be unique' });
  }
  for (const trigger of macroTriggerSchema.options) {
    for (const graph of [value.graphs[trigger]?.draft, value.graphs[trigger]?.locked, value.legacyTrees?.[trigger]?.graph]) {
      if (graph && (('campaignId' in graph && graph.campaignId !== value.sourceCampaignId) || graph.trigger !== trigger)) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['graphs', trigger], message: 'Archive graph identity must match its source and trigger' });
      }
    }
  }
}).transform(value => value as CampaignMacroArchive);
const revisionMap = z.object({ launch: revision.optional(), watcher: revision.optional() }).strict();
export interface CampaignMacroImportRequest {
  archive: CampaignMacroArchive; expectedSourceRevs: Partial<Record<'launch' | 'watcher', number>>;
  expectedRuntimeRevs: Partial<Record<'launch' | 'watcher', number>>;
}
export const campaignMacroImportRequestSchema: z.ZodType<CampaignMacroImportRequest, z.ZodTypeDef, unknown> = z.object({ archive: campaignMacroArchiveSchema,
  expectedSourceRevs: revisionMap, expectedRuntimeRevs: revisionMap }).strict().superRefine((value, context) => {
  for (const trigger of macroTriggerSchema.options) {
    if ((value.archive.graphs[trigger] || value.archive.legacyTrees?.[trigger]) &&
      (value.expectedSourceRevs[trigger] === undefined || value.expectedRuntimeRevs[trigger] === undefined)) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: `Both destination revision maps must include ${trigger}` });
    }
  }
}).transform(value => value as CampaignMacroImportRequest);
export const campaignMacroImportResponseSchema = z.object({ importedDraftRevs: revisionMap }).strict();
export const macroMigrationRequestSchema = z.object({ expectedLegacyRevision: revision }).strict();
